import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ImageShapes() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔹 Advanced CSS: Image Shapes</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS allows you to create interesting image shapes using{" "}
        <code>border-radius</code> and <code>clip-path</code>. You can make
        circles, ellipses, polygons, and combine with shadows or filters for
        advanced designs.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>border-radius</code> can make circles or rounded shapes.
          </li>
          <li>
            <code>clip-path</code> allows complex shapes like polygons,
            ellipses, or inset.
          </li>
          <li>
            Combine shapes with <code>box-shadow</code> and <code>filter</code>{" "}
            for depth and style.
          </li>
          <li>
            Ensure proper image size to maintain aspect ratio for circles and
            ellipses.
          </li>
          <li>
            CSS Shapes can be animated or transformed for interactive effects.
          </li>
        </ul>
      </div>

      {/* Example 1: Circle */}
      <div className="tutorial-section">
        <h2 className="section-title">⚪ Circle Image</h2>
        <CodeCopyBlock
          code={`img {
  border-radius: 50%;
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=300&q=80"
          alt="Circle example"
          style={{ borderRadius: "50%", width: "150px", marginTop: "1rem" }}
        />
      </div>

      {/* Example 2: Ellipse */}
      <div className="tutorial-section">
        <h2 className="section-title">🔵 Ellipse Image</h2>
        <CodeCopyBlock
          code={`img {
  border-radius: 50% / 25%;
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=300&q=80"
          alt="Ellipse example"
          style={{
            borderRadius: "50% / 25%",
            width: "200px",
            marginTop: "1rem",
          }}
        />
      </div>

      {/* Example 3: Polygon Shape */}
      <div className="tutorial-section">
        <h2 className="section-title">🔺 Polygon Shape</h2>
        <CodeCopyBlock
          code={`img {
  clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1526772662000-3f88f10405ff?auto=format&fit=crop&w=300&q=80"
          alt="Polygon example"
          style={{
            clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
            width: "200px",
            marginTop: "1rem",
          }}
        />
      </div>

      {/* Example 4: Circle + Shadow */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 Circle with Shadow</h2>
        <CodeCopyBlock
          code={`img {
  border-radius: 50%;
  box-shadow: 5px 5px 15px rgba(0,0,0,0.4);
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=300&q=80"
          alt="Circle shadow example"
          style={{
            borderRadius: "50%",
            boxShadow: "5px 5px 15px rgba(0,0,0,0.4)",
            width: "150px",
            marginTop: "1rem",
          }}
        />
      </div>

      {/* Example 5: Combined Filters + Shape */}
      <div className="tutorial-section">
        <h2 className="section-title">⚡ Combined Effects</h2>
        <CodeCopyBlock
          code={`img {
  border-radius: 30%;
  filter: grayscale(50%) brightness(120%);
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=300&q=80"
          alt="Combined effects example"
          style={{
            borderRadius: "30%",
            filter: "grayscale(50%) brightness(120%)",
            width: "200px",
            marginTop: "1rem",
          }}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Experiment with complex <code>clip-path</code> shapes like stars or
            custom polygons.
          </li>
          <li>Combine shapes with hover animations and CSS transitions.</li>
          <li>Use masks for more advanced image shaping effects.</li>
          <li>
            Combine filters, shadows, and transforms with shapes for creative
            designs.
          </li>
          <li>
            Ensure images remain responsive and maintain aspect ratio inside
            shapes.
          </li>
        </ul>
      </div>
    </div>
  );
}
